import { create } from 'zustand';
import type { SocialProfile } from '@teaplus/shared';

/** A friend's message that arrived during the film, shown as a card you can reply in. */
export interface PlayerChatCard {
  id: string;
  from: SocialProfile;
  body: string;
  attachment: 'gif' | 'image' | null;
}

interface PlayerChatState {
  /** How many full-size players that can show the mini chat are on screen (0 or 1, in practice). */
  hosts: number;
  /** The chat open over the film, if any. */
  friend: SocialProfile | null;
  /** Shrunk to a bubble in the corner. */
  minimised: boolean;
  card: PlayerChatCard | null;
  /** The card shrunk to a pill. */
  cardMinimised: boolean;
}

const usePlayerChatStore = create<PlayerChatState>(() => ({
  hosts: 0,
  friend: null,
  minimised: false,
  card: null,
  cardMinimised: false,
}));

export const usePlayerChat = usePlayerChatStore;

/** Called by the player while it can show the mini chat. Closing the last one closes the chat. */
export function hostPlayerChat() {
  usePlayerChatStore.setState((s) => ({ hosts: s.hosts + 1 }));
  return () =>
    usePlayerChatStore.setState((s) =>
      s.hosts > 1
        ? { hosts: s.hosts - 1 }
        : { hosts: 0, friend: null, minimised: false, card: null, cardMinimised: false },
    );
}

/** True when a chat message should go to the player rather than the usual card in the corner. */
export const playerChatShown = () => usePlayerChatStore.getState().hosts > 0;

export function openPlayerChat(friend: SocialProfile) {
  usePlayerChatStore.setState((s) => ({
    friend,
    minimised: false,
    card: s.card?.from.id === friend.id ? null : s.card,
  }));
}

/** Shrinks the chat to a bubble, or opens it again (taking that friend's card with it). */
export const minimisePlayerChat = (minimised: boolean) =>
  usePlayerChatStore.setState((s) => ({
    minimised,
    card: !minimised && s.card?.from.id === s.friend?.id ? null : s.card,
  }));

export const closePlayerChat = () =>
  usePlayerChatStore.setState({ friend: null, minimised: false });

export const dismissPlayerChatCard = () =>
  usePlayerChatStore.setState({ card: null, cardMinimised: false });

export const minimisePlayerChatCard = (cardMinimised: boolean) =>
  usePlayerChatStore.setState({ cardMinimised });

/**
 * A message arrived while the player shows the mini chat, and isn't already on screen in it
 * (the chat is shrunk, or it's someone else): a card to reply in, unless notifications are hidden.
 */
export function receivePlayerChat(card: PlayerChatCard, hidden: boolean) {
  if (!hidden) usePlayerChatStore.setState({ card, cardMinimised: false });
}
